import ResouceModal from '@/pages/ChatAppPage/components/ResouceModal'
import ThumbsMessage from '@/pages/ChatAppPage/components/ThumbsMessage'
import { useEffect, useRef } from 'react'
import MessageBs from './MessageBs'
import MessageSystem from './MessageSystem'
import MessageUser from './MessageUser'
import RunLog from './RunLog'
import { useChatStore } from './chatStore'

/**
 * 消息面板组件
 *
 * 这是聊天界面的核心组件，负责显示所有聊天消息，包括用户消息、助手回复、系统消息等。
 * 支持自动滚动、历史消息加载、消息类型识别等功能。
 *
 * 功能特点：
 * - 消息列表展示和渲染
 * - 自动滚动到底部
 * - 智能滚动锁定机制
 * - 历史消息加载
 * - 消息类型工厂模式
 * - 溯源信息处理
 * - 反馈和资源模态框
 *
 * 使用场景：
 * 1. 聊天界面 - 显示完整的对话历史
 * 2. 消息流展示 - 实时显示流式消息
 * 3. 历史记录 - 展示历史聊天记录
 * 4. 多类型消息 - 支持文本、图片、文件等多种消息类型
 * 5. 交互反馈 - 提供点赞、溯源等交互功能
 * 6. 响应式布局 - 根据推荐问题状态调整布局
 * 7. 无限滚动 - 支持加载更多历史消息
 * 8. 状态管理 - 与聊天状态管理集成
 */

/**
 * 消息面板组件属性接口
 *
 * @param useName - 用户显示名称
 * @param loadMore - 加载更多历史消息的回调函数
 * @param recommendedHid - 推荐问题是否隐藏
 * @param recommendedShown - 推荐问题是否实际显示
 * @param conversation - 会话内容
 * @param chatting - 聊天状态
 * @param recommendVisible - 是否允许显示推荐问题
 * @param showMcpOutput - 是否显示MCP输出
 * @param conversationId - 会话ID
 * @param logo - 助手Logo
 */
export default function MessagePanne({
  useName,
  loadMore,
  recommendedHid,
  recommendedShown,
  conversation = '',
  chatting = '',
  recommendVisible = true,
  showMcpOutput = false,
  conversationId = '',
  logo = ''
}) {
  // 从聊天状态管理中获取相关状态
  const { chatId, messages, running, lastIsEndSuccess } = useChatStore()
  // 反馈
  const thumbRef = useRef(null)
  // 溯源模态框引用
  const sourceRef = useRef(null)

  // 消息容器引用，用于滚动控制
  const messagesRef = useRef(null)
  // 滚动锁定状态，防止用户滚动时自动滚动干扰
  const scrollLockRef = useRef(false)

  /**
   * 会话ID变化时重置滚动状态
   * 新会话开始时，重置滚动锁定和查询锁定状态
   */
  useEffect(() => {
    scrollLockRef.current = false
    queryLockRef.current = false
  }, [chatId])

  /**
   * 消息变化时自动滚动到底部
   * 当有新消息时，如果用户没有手动滚动，则自动滚动到底部
   */
  useEffect(() => {
    // 如果用户不在底部，不自动滚动
    if (scrollLockRef.current) return

    // 自动滚动到底部
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
      // console.log('📩 接收消息，自动滚动到底部')
    }
  }, [messages])

  /**
   * 会话结束时强制滚动到底部
   * 当会话结束时，无论用户是否在底部，都滚动到底部并解锁滚动
   */
  useEffect(() => {
    if (!running && lastIsEndSuccess && messagesRef.current) {
      // console.log('✅ 会话结束，强制滚动到底部')
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight
      scrollLockRef.current = false
    }
  }, [running, lastIsEndSuccess])

  // 查询锁定状态，防止重复加载更多消息
  const queryLockRef = useRef(false)

  /**
   * 用户滚动检测和加载更多历史消息
   * 监听用户滚动行为，实现智能滚动锁定和无限滚动加载
   */
  useEffect(() => {
    function handleScroll() {
      if (queryLockRef.current) return
      const { scrollTop, clientHeight, scrollHeight } = messagesRef.current

      // 检测用户是否在底部（距离底部50px以内认为在底部）
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight
      const isAtBottom = distanceFromBottom <= 50

      // 智能滚动锁定机制：
      // 1. 用户向上滚动就停止自动滚动
      // 2. 用户滚动到底部开启自动滚动
      if (isAtBottom) {
        if (scrollLockRef.current) {
          // console.log('🔓 用户滚动到底部，开启自动滚动')
          scrollLockRef.current = false
        }
      } else {
        if (!scrollLockRef.current) {
          // console.log('🔒 用户向上滚动，停止自动滚动')
          scrollLockRef.current = true
        }
      }

      // 无限滚动加载更多历史消息
      // 当滚动到顶部90px以内时，触发加载更多
      if (messagesRef.current.scrollTop <= 90) {
        queryLockRef.current = true
        loadMore()
        // 防抖处理
        setTimeout(() => {
          queryLockRef.current = false
        }, 1000)
      }
    }

    // 添加滚动事件监听器
    messagesRef.current?.addEventListener('scroll', handleScroll)
    return () => {
      // 清理事件监听器
      messagesRef.current?.removeEventListener('scroll', handleScroll)
    }
  }, [messagesRef.current, messages, chatId])

  /**
   * 动态计算底部内边距
   * 根据推荐问题的实际显示状态和会话状态，动态调整消息容器的底部内边距
   * 确保推荐问题不会遮挡消息内容
   */
  const paddingBottom = conversation
    ? recommendedShown
      ? 'pb-[420px]'
      : 'pb-[280px]'
    : recommendedShown
    ? 'pb-[380px]'
    : 'pb-[248px]'
    ? 'pb-[380px]'
    : 'pb-[248px]'
  // const paddingBottom = conversation
  //   ? recommendedHid
  //     ? 'pb-[420px]'
  //     : 'pb-[280px]'
  //   : recommendedHid
  //   ? 'pb-[280px]'
  //   : 'pb-[140px]'

  /**
   * 获取溯源信息
   * 优先使用上一条消息的溯源信息，如果没有则使用当前消息的溯源信息
   *
   * @param prevMessages - 上一条消息
   * @param currentMessage - 当前消息
   * @returns 溯源信息数组
   */
  const getCitations = (prevMessages, currentMessage) => {
    return prevMessages?.citations?.length > 0 ? prevMessages.citations : currentMessage?.content?.[0]?.citations ?? []
  }

  /**
   * 获取命中的插件信息
   * 优先检查当前消息的插件信息，如果没有则检查上一条消息的插件信息
   *
   * @param prevMessages - 上一条消息
   * @param currentMessage - 当前消息
   * @returns 插件信息对象
   */
  const getHitPlugin = (prevMessages, currentMessage) => {
    // 检查当前消息的 content 中是否包含 hitPlugin
    if (currentMessage?.content?.[0]?.hitPlugin) {
      return currentMessage.content[0].hitPlugin
    }

    // 检查上一条消息中是否包含 hitPlugin
    if (prevMessages?.content?.[0]?.hitPlugin) {
      return prevMessages.content[0].hitPlugin
    }

    // 如果都没有，返回空对象
    return {}
  }

  // 根据会话状态设置背景色
  const backGrou = conversation ? 'bg-[#ffffff]' : 'bg-[#FFFFFF]'
  return (
    <div
      id="message-panne"
      ref={messagesRef}
      className={` overflow-y-auto pt-[48px] scrollbar-hide ${paddingBottom}`}
      style={{ height: 'calc(100% - 20px)' }}
    >
      {/* {guideWord && (
        <MessageBs key={9999} data={{ message: guideWord, isSend: false, chatKey: '', end: true, user_name: '' }} />
      )} */}
      {/* 消息列表渲染 - 使用工厂模式根据消息类型渲染不同组件 */}
      {messages.map((msg: any, index) => {
        // 消息类型工厂：根据消息角色确定渲染类型
        let type = 'llm' // 默认为LLM消息
        if (msg.role === 'user') {
          type = 'user'
        } else if (msg.role === 'system') {
          type = 'system'
        } else if (msg.role === 'citations') {
          return '' // 溯源消息不显示
          // type = 'runLog'
        }

        // 根据消息类型渲染对应的组件
        switch (type) {
          case 'user':
            // 用户消息组件
            return <MessageUser key={index} userName={useName} data={msg.content} />
          case 'llm':
            // AI助手消息组件
            return (
              <MessageBs
                chatting={chatting}
                backGrou={backGrou}
                key={index}
                citations={getCitations(messages[index - 1], msg)}
                hitPlugin={getHitPlugin(messages[index - 1], msg)}
                data={msg.content[0]}
                showMcpOutput={showMcpOutput}
                nextDataInfo={{
                  // 如果携带citations上一条没有，找最近的user消息
                  nextData:
                    messages
                      .slice()
                      .reverse()
                      .find(message => message.role === 'user') || {},
                  index,
                  MaxLength: messages.length,
                  data: msg.content
                }}
                lastUserInfo={
                  messages
                    .slice(0, index)
                    .reverse()
                    .find(message => message.role === 'user') || {}
                }
                onUnlike={chatId => {
                  thumbRef.current?.openModal(chatId)
                }}
                onSource={data => {
                  sourceRef.current?.openModal(data)
                }}
                conversationId={conversationId}
                conversation={conversation}
                logo={logo}
              />
            )
          case 'system':
            // 系统消息组件
            return <MessageSystem key={index} data={msg.content[0]} logo={logo} />
          case 'runLog':
            // 运行日志组件
            return <RunLog key={index} data={msg.citations} />
          default:
            // 未知消息类型的默认显示
            return (
              <div className="mt-2 rounded-md border p-2 text-sm" key={index}>
                未知消息类型
              </div>
            )
        }
      })}

      {/* 反馈模态框组件 */}
      <ThumbsMessage ref={thumbRef}></ThumbsMessage>

      {/* 溯源资源模态框组件 */}
      <ResouceModal ref={sourceRef}></ResouceModal>
    </div>
  )
}
